import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LessonProgress {
  lessonId: string;
  progress: number;
  completed: boolean;
  lastUpdated: string;
  timeSpent: number;
  quizScore?: number;
  quizSubmitted: boolean;
  quizAnswers?: { [questionIndex: number]: number };
}

interface CourseProgress {
  courseId: string;
  lastLessonId: string;
  lastLessonProgress: number;
  lastUpdated: string;
  completed: boolean;
  lessons?: { [lessonId: string]: LessonProgress };
}

export const useCourseProgress = (courseId: string) => {
  const { currentUser } = useAuth();
  const [courseProgress, setCourseProgress] = useState<CourseProgress | null>(null);
  const [lessonsProgress, setLessonsProgress] = useState<{ [lessonId: string]: LessonProgress }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load course progress
  const loadCourseProgress = useCallback(async () => {
    if (!currentUser?.uid || !courseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { doc, getDoc, collection, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      // Load main course progress
      const progressRef = doc(db, 'users', currentUser.uid, 'CourseProgress', courseId);
      const progressDoc = await getDoc(progressRef);
      
      if (progressDoc.exists()) {
        const data = progressDoc.data() as CourseProgress;
        setCourseProgress(data);
      }
      
      // Load all lesson progress
      const lessonsRef = collection(db, 'users', currentUser.uid, 'CourseProgress', courseId, 'Lessons');
      const lessonsSnapshot = await getDocs(lessonsRef);
      
      const lessons: { [lessonId: string]: LessonProgress } = {};
      lessonsSnapshot.forEach(doc => {
        lessons[doc.id] = doc.data() as LessonProgress;
      });
      
      setLessonsProgress(lessons);
      
    } catch (err) {
      console.error('Error loading course progress:', err);
      setError('Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, courseId]);

  // Save lesson progress
  const saveLessonProgress = useCallback(async (
    lessonId: string, 
    progress: number, 
    additionalData: Partial<LessonProgress> = {}
  ) => {
    if (!currentUser?.uid || !courseId || !lessonId) return;
    
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const lessonProgressRef = doc(db, 'users', currentUser.uid, 'CourseProgress', courseId, 'Lessons', lessonId);
      
      const lessonData: LessonProgress = {
        lessonId,
        progress,
        completed: progress >= 100,
        lastUpdated: new Date().toISOString(),
        timeSpent: 0,
        quizSubmitted: false,
        ...additionalData
      };
      
      await setDoc(lessonProgressRef, lessonData, { merge: true });
      
      // Update local state
      setLessonsProgress(prev => ({
        ...prev,
        [lessonId]: lessonData
      }));
      
      // Update course progress
      const progressRef = doc(db, 'users', currentUser.uid, 'CourseProgress', courseId);
      await setDoc(progressRef, {
        courseId,
        lastLessonId: lessonId,
        lastLessonProgress: progress,
        lastUpdated: new Date().toISOString(),
        completed: progress >= 100
      }, { merge: true });
      
      setCourseProgress(prev => prev ? {
        ...prev,
        lastLessonId: lessonId,
        lastLessonProgress: progress,
        lastUpdated: new Date().toISOString(),
        completed: progress >= 100
      } : null);
      
      return true;
      
    } catch (err) {
      console.error('Error saving lesson progress:', err);
      setError('Failed to save progress');
      return false;
    }
  }, [currentUser?.uid, courseId]);

  // Get overall course progress
  const getOverallProgress = useCallback(() => {
    const lessonIds = Object.keys(lessonsProgress);
    if (lessonIds.length === 0) return 0;
    
    const totalProgress = lessonIds.reduce((sum, lessonId) => {
      return sum + (lessonsProgress[lessonId]?.progress || 0);
    }, 0);
    
    return Math.round(totalProgress / lessonIds.length);
  }, [lessonsProgress]);

  // Get lesson progress
  const getLessonProgress = useCallback((lessonId: string) => {
    return lessonsProgress[lessonId] || null;
  }, [lessonsProgress]);

  // Check if lesson is completed
  const isLessonCompleted = useCallback((lessonId: string) => {
    return lessonsProgress[lessonId]?.completed || false;
  }, [lessonsProgress]);

  // Load progress on mount
  useEffect(() => {
    loadCourseProgress();
  }, [loadCourseProgress]);

  return {
    courseProgress,
    lessonsProgress,
    loading,
    error,
    loadCourseProgress,
    saveLessonProgress,
    getOverallProgress,
    getLessonProgress,
    isLessonCompleted
  };
};
